import clsx from 'clsx';
import * as React from 'react';
import { offset, useRole, useHover, useDismiss, useFloating, safePolygon, useInteractions } from '@floating-ui/react-dom-interactions';
import { colors } from '@ovpn-ui/styles';
import { useMergeRefs } from '@floating-ui/react';
import Text from '../Text';
import useKeyboardFocus from './useKeyboardFocus';
import { StyledTooltip } from './Tooltip.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Tooltip(props, ref) {
  var _trigger$props, _trigger$props2;
  const {
    children,
    trigger,
    className,
    placement = 'bottom',
    arrow: withArrow = true,
    open: _open = undefined,
    onChange,
    interactive = false,
    enterDelay = 200,
    leaveDelay = 200,
    disableBypass = false,
    p = '8px 16px',
    maxWidth = 320,
    middlewares,
    ...htmlAttributes
  } = props;
  const [open, setOpen] = React.useState(false);
  const middleware = React.useMemo(() => [offset(8), ...(middlewares || [])], [middlewares]);
  const bypass = !disableBypass && !children;
  const {
    reference,
    floating,
    context
  } = useFloating({
    open,
    onOpenChange: nextOpen => {
      setOpen(nextOpen);
      onChange == null ? void 0 : onChange(nextOpen);
    }
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([useHover(context, {
    handleClose: interactive ? safePolygon({
      blockPointerEvents: false
    }) : null,
    delay: {
      open: enterDelay,
      close: leaveDelay
    },
    enabled: !bypass
  }), useKeyboardFocus(context, {
    enabled: !bypass
  }), useRole(context, {
    role: 'tooltip',
    enabled: !bypass
  }), useDismiss(context, {
    enabled: !bypass
  })]);
  const anchorRef = React.useRef(null);
  const combinedRef = useMergeRefs([ref, trigger == null ? void 0 : trigger.ref, anchorRef, reference]);
  const boxProps = {
    p,
    maxWidth
  };
  if (bypass) {
    return /*#__PURE__*/React.cloneElement(trigger, {
      ref
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/React.cloneElement(trigger, getReferenceProps({
      ...trigger.props,
      ref: combinedRef,
      style: {
        cursor: (_trigger$props = trigger.props) != null && _trigger$props.disabled ? 'default' : 'pointer',
        ...((_trigger$props2 = trigger.props) == null ? void 0 : _trigger$props2.style)
      }
    })), /*#__PURE__*/_jsx(StyledTooltip, {
      className: clsx('ovpn-tooltip', className),
      placement: placement,
      open: _open != null ? _open : open,
      anchorEl: anchorRef.current,
      $interactive: interactive,
      ref: floating,
      arrow: withArrow,
      middlewares: middleware,
      arrowPadding: 12,
      ...getFloatingProps(),
      ...htmlAttributes,
      ...boxProps,
      children: typeof children === 'string' ? /*#__PURE__*/_jsx(Text, {
        variant: "textSm",
        weight: "medium",
        color: colors.neutralWhite,
        children: children
      }) : children
    })]
  });
}
export default /*#__PURE__*/React.forwardRef(Tooltip);